import { action, observable, makeObservable } from "mobx";
import AddressAPI from "../api/AddressAPI";
import {
  Address,
  AddressRequestBody,
  City,
  Country,
  State,
} from "../models/Address/Address";
import { FilterValue } from "../models/Filters/FilterValue";
import {
  GetCityParams,
  GetCountryParams,
  GetStateParams,
} from "../models/Address/getAddressParams";
import { AddressFilters } from "../models/Address/AddressFilters";

export class AddressStore {
  addressAPI: AddressAPI;
  addressDetails: AddressRequestBody;
  originalAddress: AddressRequestBody;
  cityDetails: City;
  stateDetails: State;
  countryDetails: Country;
  limit: number;
  total_pages: number;
  total_count: number;
  address: Address;
  addresses: Address[];
  ownAddresses: Address[];
  isLoggedIn: Boolean;
  loading: boolean;
  ids: number[];
  cities: City[];
  selectedCountry: FilterValue[];
  selectedState: FilterValue[];
  cityName: string;
  manualStateName: string;
  manualStateCode: number | null;
  popupCountryFilter: FilterValue[];
  popupStateFilter: FilterValue[];

  constructor() {
    this.addressAPI = new AddressAPI();
    this.isLoggedIn = false;
    this.loading = false;
    this.limit = 0;
    this.total_pages = 0;
    this.total_count = 0;
    this.addressDetails = {} as AddressRequestBody;
    this.originalAddress = {} as AddressRequestBody;
    this.cityDetails = {} as City;
    this.stateDetails = {} as State;
    this.countryDetails = {} as Country;
    this.address = {} as Address;
    this.addresses = [];
    this.ids = [];
    this.cities = [];
    this.ownAddresses = [];
    this.selectedCountry = [];
    this.selectedState = [];
    this.cityName = "";
    this.manualStateName = "";
    this.manualStateCode = null;
    this.popupCountryFilter = [];
    this.popupStateFilter = [];

    makeObservable(this, {
      addresses: observable,
      addressDetails: observable,
      cityDetails: observable,
      stateDetails: observable,
      countryDetails: observable,
      loading: observable,
      discardChanges: action,
      removeAddressDetails: action,
      deleteAddress: action,
      updateLocalAddressDetails: action,
      reset: action,
      updateFirstName: action,
      updateAddressDetails: action,
      createAddress: action,
      createCity: action,
      getCity: action,
      getCountry: action,
      getState: action,
      updateCity: action,
      updateState: action,
      updateCountry: action,
      clearAddressDetails: action,
      clearAddresses: action,
      cities: observable,
      ownAddresses: observable,
      getOwnAddresses: action,
      selectedCountry: observable,
      selectedState: observable,
      cityName: observable,
      manualStateName: observable,
      manualStateCode: observable,
      popupCountryFilter: observable,
      popupStateFilter: observable,
      setCityName: action,
      setManualStateName: action,
      setManualStateCode: action,
      setSelectedCountry: action,
      setSelectedState: action,
      setPopupCountryFilter: action,
      setPopupStateFilter: action,
      address: observable,
      getDefaultBillFromAddress: action,
      getAddressById: action,
    });
  }

  reset = () => {
    this.addressAPI = new AddressAPI();
    this.isLoggedIn = false;
    this.loading = false;
    Object.assign(this, new AddressStore());
  };

  removeAddressDetails = () => {
    // this.addressDetails = undefined;
  };

  updateLocalAddressDetails = (address: AddressRequestBody) => {
    this.addressDetails = address;
    this.originalAddress = address;
  };

  clearAddressDetails = () => {
    this.originalAddress = {};
    this.addressDetails = {};
  };

  // getCustomerDetailsByCustomerId = async (customerId: number | null) => {
  //   try {
  //     const response = await this.addressAPI.getCustomerDetailsByCustomerId(
  //       customerId!
  //     );
  //     this.updateLocalAddressDetails(response);
  //   } catch (error: any) {
  //     throw error;
  //   }
  // };

  getDefaultBillFromAddress = async (customer_id: number) => {
    try {
      const response = await this.addressAPI.getDefaultBillFromAddress(
        customer_id
      );
      this.address = response;
    } catch (error: any) {
      throw error;
    }
  };

  getAddressById = async (id: number) => {
    try {
      const response = await this.addressAPI.getAddressById(id);
      this.updateLocalAddressDetails(response);
    } catch (error: any) {
      throw error;
    }
  };

  createAddress = async () => {
    try {
      const addressRequest: AddressRequestBody = {
        label: this.addressDetails.label ?? "",
        customer_id: this.addressDetails.customer_id ?? 0,
        userId: this.addressDetails.userId ?? 0,
        first_name: this.addressDetails.first_name ?? "",
        last_name: this.addressDetails.last_name ?? "",
        address1: this.addressDetails.address1 ?? "",
        address2: this.addressDetails.address2 ?? "",
        address3: this.addressDetails.address3 ?? "",
        city_id: this.addressDetails.city_id ?? 0,
        city_name: this.addressDetails.city_name ?? "",
        state_id: this.addressDetails.state_id ?? 0,
        state_name: this.addressDetails.state_name ?? "",
        country_id: this.addressDetails.country_id ?? 0,
        country_name: this.addressDetails.country_name ?? "",
        postal_code: this.addressDetails.postal_code ?? "",
        email: this.addressDetails.email ?? "",
        contact_no: this.addressDetails.contact_no ?? "",
        gstin: this.addressDetails.gstin ?? "",
        receiver: this.addressDetails.receiver ?? "",
        isHeadOffice: this.addressDetails.isHeadOffice ?? false,
      };

      const response = await this.addressAPI.createAddress(addressRequest);
      // Update customer details based on the response
      this.addressDetails = response.result || [];
      return response;
    } catch (error: any) {
      console.error("Error creating address:", error);
      throw error;
    }
  };

  updateAddressDetails = async () => {
    if (!this.addressDetails.id) return;
    try {
      const addressRequest: AddressRequestBody = {
        id: this.addressDetails.id,
        label: this.addressDetails.label ?? "",
        customer_id: this.addressDetails.customer_id,
        userId: this.addressDetails.userId ?? 0,
        first_name: this.addressDetails.first_name ?? "",
        last_name: this.addressDetails.last_name ?? "",
        address1: this.addressDetails.address1 ?? "",
        address2: this.addressDetails.address2 ?? "",
        address3: this.addressDetails.address3 ?? "",
        city_id: this.addressDetails.city_id ?? 0,
        city_name: this.addressDetails.city_name ?? "",
        state_id: this.addressDetails.state_id ?? 0,
        state_name: this.addressDetails.state_name ?? "",
        country_id: this.addressDetails.country_id ?? 0,
        country_name: this.addressDetails.country_name ?? "",
        postal_code: this.addressDetails.postal_code ?? "",
        email: this.addressDetails.email ?? "",
        contact_no: this.addressDetails.contact_no ?? "",
        gstin: this.addressDetails.gstin ?? "",
        receiver: this.addressDetails.receiver ?? "",
        isHeadOffice: this.addressDetails.isHeadOffice ?? false,
      };

      // Send the request to the API
      const response = await this.addressAPI.updateAddressDetails(
        addressRequest
      );

      if (response.success) {
        this.updateLocalAddressDetails(response.result);
        this.addressDetails = response.result || [];
      }

      return response;
    } catch (error: any) {
      console.error("Error creating address:", error);
      throw error;
    }
  };
  updateFirstName = (name: string) => {
    if (this.addressDetails) {
      this.addressDetails.first_name = name;
    }
  };

  updateLastName = (name: string) => {
    if (this.addressDetails) {
      this.addressDetails.last_name = name;
    }
  };

  updateGST = (name: string) => {
    if (this.addressDetails) {
      this.addressDetails.gstin = name;
    }
  };
  updateEmail = (name: string) => {
    if (this.addressDetails) {
      this.addressDetails.email = name;
    }
  };
  updateContact = (name: string) => {
    if (this.addressDetails) {
      this.addressDetails.contact_no = name;
    }
  };

  updateCity = (selectedValue?: FilterValue[]) => {
    if (selectedValue && selectedValue.length !== 0)
      this.addressDetails = {
        ...this.addressDetails,
        city_id: Number(selectedValue[0].id),
        city_name: selectedValue[0].value,
      };
    else
      this.addressDetails = {
        ...this.addressDetails,
        city_id: undefined,
        city_name: undefined,
      };
  };

  updateState = (selectedValue?: FilterValue[]) => {
    if (selectedValue && selectedValue.length !== 0)
      this.addressDetails = {
        ...this.addressDetails,
        state_id: Number(selectedValue[0].id),
        state_name: selectedValue[0].value,
      };
    else
      this.addressDetails = {
        ...this.addressDetails,
        state_id: undefined,
        state_name: undefined,
      };
  };

  updateCountry = (selectedValue: FilterValue[]) => {
    if (selectedValue && selectedValue.length !== 0)
      this.addressDetails = {
        ...this.addressDetails,
        country_id: Number(selectedValue[0].id),
        country_name: selectedValue[0].value,
      };
    else
      this.addressDetails = {
        ...this.addressDetails,
        country_id: undefined,
        country_name: undefined,
      };
  };

  updateCustomer = (customer?: FilterValue) => {
    if (this.addressDetails) {
      if (customer) {
        this.addressDetails.customer_id = Number(customer.id);
        this.addressDetails.customer_name = customer.value;
      } else {
        this.addressDetails.customer_id = undefined;
        this.addressDetails.customer_name = undefined;
      }
    }
  };

  updatePostalCode = (postal_code: string) => {
    if (this.addressDetails) {
      this.addressDetails.postal_code = postal_code;
    }
  };
  updateAddressThree = (name: string) => {
    if (this.addressDetails) {
      this.addressDetails.address3 = name;
    }
  };
  updateAddressTwo = (name: string) => {
    if (this.addressDetails) {
      this.addressDetails.address2 = name;
    }
  };
  updateAddressOne = (name: string) => {
    if (this.addressDetails) {
      this.addressDetails.address1 = name;
    }
  };

  getCity = async (filter: GetCityParams): Promise<City[]> => {
    try {
      const response = await this.addressAPI.getCity(filter);
      this.cities = response.list;
      return response.list;
    } catch (error: any) {
      throw error;
    }
  };

  getCountry = async (filter: GetCountryParams): Promise<Country[]> => {
    try {
      const response = await this.addressAPI.getCountry(filter);
      return response.list;
    } catch (error: any) {
      throw error;
    }
  };

  getState = async (filter: GetStateParams): Promise<State[]> => {
    try {
      const response = await this.addressAPI.getState(filter);
      return response.list;
    } catch (error: any) {
      throw error;
    }
  };

  deleteAddress = async (id: number) => {
    try {
      const response = await this.addressAPI.deleteAddress(id);
      if (response.success && this.addressDetails) {
        const index = this.addresses.findIndex((item) => item.id === id);
        if (index !== -1) {
          this.addresses[index] = {
            ...this.addresses[index],
          };
        }
      }
      return response;
    } catch (error: any) {
      console.error("Error creating customer:", error);
      throw error;
    }
  };

  discardChanges = async () => {
    this.addressDetails = { ...this.originalAddress };
  };

  getAddress = async (filter: AddressFilters) => {
    try {
      const response = await this.addressAPI.getAddresses(filter);
      this.addresses = response.list;
      this.limit = filter.limit!;
      this.total_pages = response.total_pages;
      this.total_count = response.total_count;
    } catch (error: any) {}
  };

  getOwnAddresses = async () => {
    try {
      const response = await this.addressAPI.getAddresses({
        limit: 100,
        page_no: 1,
        customer_id: -1,
      });
      this.ownAddresses = response.list;
    } catch (error: any) {}
  };

  updateCustomerId = (customer?: FilterValue) => {
    if (customer) {
      this.addressDetails.customer_id = Number(customer.id);
      this.addressDetails.customer_name = customer.value;
    } else {
      this.addressDetails.customer_id = undefined;
      this.addressDetails.customer_name = undefined;
    }
  };

  clearAddresses = () => {
    this.addresses = [];
  };
  setSelectedCountry = (country: FilterValue[]) => {
    this.selectedCountry = country;
  };
  setSelectedState = (state: FilterValue[]) => {
    this.selectedState = state;
  };
  setCityName = (cityName: string) => {
    this.cityName = cityName;
  };
  setManualStateName = (stateName: string) => {
    this.manualStateName = stateName;
  };
  setManualStateCode = (stateCode: number) => {
    this.manualStateCode = stateCode;
  };
  setPopupCountryFilter = (country: FilterValue[]) => {
    this.popupCountryFilter = country;
  };
  setPopupStateFilter = (state: FilterValue[]) => {
    this.popupStateFilter = state;
  };

  createCity = async (cityData: {
    cityName: string;
    stateId?: number;
    stateName?: string;
    country_id: number;
    stateCode?: number;
  }) => {
    try {
      const response = await this.addressAPI.createCity(cityData);
      return response;
    } catch (error: any) {
      throw error;
    }
  };
}
